/**
 * lightboxStore — singleton image-preview state, replacing H5 main.js:8848-8889
 * `lightbox` mutable closure + `openLightbox/closeLightbox/lightboxStep/syncLightbox`
 * imperative DOM mutation chain.
 *
 * H5 design (legacy):
 *   - module-scope `lightbox = { images: [], index: 0 }` mutated directly
 *   - `els.imageLightbox.hidden = false; rAF(() => add("is-open"))` (AP2 anti-pattern)
 *   - `document.addEventListener("keydown", ...)` global listener never cleaned up
 *     (AP9 anti-pattern: lives forever in main.js bind block lines 3401-3404)
 *   - imperative `els.lightboxImage.src = ...` per render
 *
 * React design (this store):
 *   - Zustand store carries `images`, `index`, `isOpen` plus `open/close/step`
 *     actions; the `<Lightbox>` Portal subscribes and renders declaratively
 *   - Keyboard listeners attached only while `isOpen=true` and cleaned up via
 *     useEffect return (see Lightbox.tsx)
 *   - No setTimeout fade dance: visibility is driven by isOpen; CSS handles
 *     the opacity transition on attribute change (AP2-compliant).
 *
 * Stored shape mirrors H5 callsite: `openLightbox(images, startIndex)`
 * where `images` is `{src, alt}[]` (same as PaperImage / commentImages).
 */

import { create } from "zustand";

export type LightboxImage = {
  src: string;
  alt: string;
  /** Original-resolution URL (bare /api/images/:id). When present, toolbar shows "查看原图". */
  originalSrc?: string;
};

type LightboxState = {
  images: LightboxImage[];
  index: number;
  isOpen: boolean;
  showOriginal: boolean;
  open: (images: LightboxImage[], startIndex?: number) => void;
  close: () => void;
  step: (delta: number) => void;
  setShowOriginal: (value: boolean) => void;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export const useLightboxStore = create<LightboxState>((set, get) => ({
  images: [],
  index: 0,
  isOpen: false,
  showOriginal: false,
  open(images, startIndex = 0) {
    if (!images.length) return;
    set({
      images,
      index: clamp(startIndex, 0, images.length - 1),
      isOpen: true,
      showOriginal: false,
    });
  },
  close() {
    if (!get().isOpen) return;
    set({ isOpen: false });
  },
  step(delta) {
    const { images, index } = get();
    if (images.length <= 1) return;
    const len = images.length;
    set({ index: (index + delta + len) % len, showOriginal: false });
  },
  setShowOriginal(value) {
    set({ showOriginal: value });
  },
}));
